import * as React from "react";
import styled from "@emotion/styled";
import {
  Avatar,
  FlexRow,
  H3,
  Link,
  ReviewTag,
  ScreenReaderText,
  TagList,
  Text,
  EntityBadge,
  IconExternalLink,
  Stack,
} from "atoms";
import { useAuth } from "features/auth";
import { Thumbnail } from "features/images";
import { useReviewable } from "features/reviewable";
import { RatingIcon } from "./RatingIcon";
import type {
  ProjectReview,
  ArtistReview,
  ReviewVote,
  ReviewSortOption,
} from "../types";
import { editReviewPath } from "../utils/paths";

interface Props {
  review: ProjectReview | ArtistReview;
  usersVote?: ReviewVote;
  activeSort?: ReviewSortOption;
  showVotes?: boolean;
  showParent?: boolean;
  alwaysShowStatus?: boolean;
}
const getReviewerName = (isAnon: boolean, name?: string, wallet?: string) =>
  isAnon ? "Anonymous" : name ?? "Unnamed Hugger";

function Review({
  review,
  usersVote,
  activeSort,
  showVotes = true,
  showParent = false,
  alwaysShowStatus = false,
  ...props
}: Props) {
  // Store ref to component root element for scroll logic
  const reviewElement = React.useRef<HTMLElement | null>(null);

  const {
    user,
    roles: { showAdminFeatures },
  } = useAuth();

  const {
    createdAt,
    createdByUserId,
    displayName,
    displayAvatar,
    rating,
    forYouIf,
    notForYouIf,
    artTags,
    communityTags,
    founderTeamTags,
    roadmapTags,
    isAnonymous,
  } = review;

  const isProject = review.reviewableType === "PROJECT";
  const isUsersReview = user?.id === createdByUserId;
  const showStatus = alwaysShowStatus || review?.status !== "PUBLISHED";

  const reviewerName = getReviewerName(isAnonymous, displayName);
  const { data: reviewable } = useReviewable(
    review?.reviewableType ?? "",
    review?.reviewableId ?? ""
  );

  return (
    <>
      {showParent && (
        <ReviewableSummary>
          <FlexRow gap="10px">
            <SThumbnail
              src={reviewable?.heroImage}
              entityType={reviewable?.type}
            />

            <Stack gap="xxs">
              <Text as="span" size="xxs" textTransform="uppercase">
                Review for
              </Text>

              <H3 size="md">
                {reviewable?.name} <EntityBadge entityType={reviewable?.type} />
              </H3>

              <div>
                <Link
                  size="xs"
                  to={`/${isProject ? "projects" : "artists"}/${
                    reviewable?.id
                  }`}
                  target="_blank"
                >
                  View profile <IconExternalLink />
                </Link>
              </div>
            </Stack>
          </FlexRow>
        </ReviewableSummary>
      )}

      <Article showStatus={showStatus} ref={reviewElement} {...props}>
        <Header>
          <ReviewAuthor>
            <ReviewAvatar
              src={isAnonymous ? undefined : displayAvatar}
              alt={`Profile picture for ${reviewerName}`}
              loading="lazy"
            />

            <ReviewUsername size="md">
              {isAnonymous ? (
                <>
                  {/* If user is admin or user is reviewer, show username and link to public profile */}
                  {(showAdminFeatures || isUsersReview) && (
                    <>
                      <Link to={`/users/${createdByUserId}`}>
                        {displayName}
                      </Link>{" "}
                      -{" "}
                    </>
                  )}

                  {reviewerName}
                </>
              ) : (
                <Link to={`/users/${createdByUserId}`}>{reviewerName}</Link>
              )}
            </ReviewUsername>

            <ReviewRatingIcon>
              <RatingIcon review={review} aria-label="hidden" />
            </ReviewRatingIcon>

            <ReviewRatingText size="xxs">
              {rating ? (
                <>
                  <ScreenReaderText as="span">
                    Reviewer rating:
                  </ScreenReaderText>
                  {rating}
                </>
              ) : (
                <em>No Rating</em>
              )}
            </ReviewRatingText>
          </ReviewAuthor>

          <DateCreated size="xxxs">
            Created{" "}
            {new Intl.DateTimeFormat([], { dateStyle: "medium" }).format(
              new Date(createdAt)
            )}{" "}
            {showAdminFeatures && (
              <span>
                &nbsp;|&nbsp;&nbsp;
                <Link size="xs" to={editReviewPath(review)}>
                  Edit Review
                </Link>
              </span>
            )}
          </DateCreated>
        </Header>

        <div>
          <ReviewText>
            <strong>
              This {isProject ? "project" : "artist"} is for you if:
            </strong>
            {forYouIf}
          </ReviewText>

          {notForYouIf && (
            <ReviewText>
              <strong>
                This {isProject ? "project" : "artist"} is NOT for you if:
              </strong>
              {notForYouIf}
            </ReviewText>
          )}
        </div>

        {(founderTeamTags.length > 0 ||
          communityTags.length > 0 ||
          roadmapTags.length > 0 ||
          artTags.length > 0) && (
          <section>
            <TagList>
              {founderTeamTags.length > 0 && (
                <>
                  <ScreenReaderText>
                    Tags describing the project&apos;s founders:
                  </ScreenReaderText>

                  {founderTeamTags?.map((tag) => (
                    <ReviewTag variant="founder" key={tag}>
                      {tag}
                    </ReviewTag>
                  ))}
                </>
              )}

              {communityTags.length > 0 && (
                <>
                  <ScreenReaderText>
                    Tags describing the project&apos;s community:
                  </ScreenReaderText>
                  {communityTags?.map((tag) => (
                    <ReviewTag variant="community" key={tag}>
                      {tag}
                    </ReviewTag>
                  ))}
                </>
              )}

              {roadmapTags.length > 0 && (
                <>
                  <ScreenReaderText>
                    Tags describing the project&apos;s roadmap:
                  </ScreenReaderText>
                  {roadmapTags?.map((tag) => (
                    <ReviewTag variant="roadmap" key={tag}>
                      {tag}
                    </ReviewTag>
                  ))}
                </>
              )}

              {artTags.length > 0 && (
                <>
                  <ScreenReaderText>
                    Tags describing the project&apos;s art:
                  </ScreenReaderText>
                  {artTags?.map((tag) => (
                    <ReviewTag variant="art" key={tag}>
                      {tag}
                    </ReviewTag>
                  ))}
                </>
              )}
            </TagList>
          </section>
        )}
      </Article>
    </>
  );
}

type WithStatus = {
  showStatus: boolean;
};

const Article = styled.article<WithStatus>(({ theme, showStatus }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
  margin: "1rem 0 2rem",
  paddingTop: showStatus ? 0 : "2rem",
  "&+&": {
    borderTop: `1px solid ${theme.colors.fg20}`,
  },
  ".ReviewSummary &>p": {
    display: "none !important",
  },
}));

const Header = styled.header(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: ".5rem",
  [theme.breakpoints.desktop]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
}));

const ReviewAuthor = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gridTemplateRows: "1fr 45px",
  gridTemplateAreas: "'avatar username' 'avatar rating'",
  gap: "4px 1rem",
  h3: {
    margin: "0 auto 0 0",
  },
}));

const ReviewAvatar = styled(Avatar)({
  gridArea: "avatar",
  width: 50,
  height: 50,
});

const ReviewUsername = styled(H3)({
  lineHeight: 1.1,
  gridArea: "username",
  marginBottom: 0,
  transform: "translateY(8px)",
});

const ReviewRatingIcon = styled.div(({ theme }) => ({
  alignSelf: "start",
  gridArea: "avatar",
  position: "relative",
  overflow: "hidden",
  justifySelf: "end",
  display: "flex",
  transformOrigin: "bottom right",
  transform: "translate(15px, 55%)",
  width: 44,
  height: 44,
  svg: {
    height: 44,
    position: "relative",
    width: 44,
    zIndex: 2,
  },
  "&::before": {
    backgroundColor: theme.colors.bg,
    borderRadius: 999,
    boxShadow: theme.boxShadow.light,
    content: "''",
    display: "block",
    inset: 2,
    position: "absolute",
    zIndex: 1,
  },
}));

const ReviewRatingText = styled(Text)(({ theme }) => ({
  alignSelf: "center",
  gridArea: "rating",
  display: "flex",
  color: theme.colors.fg80,
  "&,*": {
    margin: 0,
  },
  em: {
    whiteSpace: "nowrap",
  },
}));

const ReviewText = styled(Text)(({ theme }) => ({
  fontSize: theme.fontSizes.xxs,
  marginBottom: 0,
  strong: {
    ...theme.fonts.display,
    color: theme.colors.fg,
    display: "block",
    fontSize: theme.fontSizes.xs,
    textTransform: "uppercase",
    fontWeight: "400",
  },
  "&+&": {
    marginTop: ".75rem",
  },
}));

const DateCreated = styled(Text)(({ theme }) => ({
  color: theme.colors.fg80,
  margin: 0,
}));

const ReviewableSummary = styled.div(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors.fg20}`,
  lineHeight: 1.2,
  paddingBottom: "1rem",
  p: {
    marginBottom: 0,
    small: {
      margin: 0,
    },
  },
  "a svg": {
    width: 13,
    height: 13,
  },
}));

const SThumbnail = styled(Thumbnail)({
  width: 66,
  height: 66,
  maxWidth: 66,
  maxHeight: 66,
});

export {
  Review,
  ReviewAuthor,
  ReviewAvatar,
  ReviewUsername,
  ReviewRatingText,
  ReviewRatingIcon,
};
